import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "@/hooks/useAuth";
import logo from "@/assets/jobyoda-logo.webp";

import InsightsIcon from "@mui/icons-material/Insights";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import SchoolIcon from "@mui/icons-material/School";
import FlagIcon from "@mui/icons-material/Flag";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LinkIcon from "@mui/icons-material/Link";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export const Route = createFileRoute("/")({
  component: Landing,
  head: () => ({
    meta: [
      { title: "JobYoda — AI-native career OS" },
      {
        name: "description",
        content:
          "Score yourself against any JD, generate tailored materials, track every application, and learn from every rejection.",
      },
      { property: "og:title", content: "JobYoda — AI-native career OS" },
      {
        property: "og:description",
        content:
          "Match scoring, tailored resumes, kanban tracking, interview practice, and rejection learning.",
      },
    ],
  }),
});

type Slide = {
  icon: React.ReactNode;
  title: string;
  body: string;
};

const SLIDES: Slide[] = [
  {
    icon: <InsightsIcon color="primary" />,
    title: "Score 1–10 vs any JD",
    body: "See your fit by skills, experience, and seniority — with the gaps to close.",
  },
  {
    icon: <DescriptionIcon color="primary" />,
    title: "Tailored resumes",
    body: "One-click rewrite that leads with what the JD asks for. No fabrication.",
  },
  {
    icon: <AutoAwesomeIcon color="primary" />,
    title: "Cover letters & Q&A",
    body: "Drafted in your voice from your verified profile in seconds.",
  },
  {
    icon: <ViewKanbanIcon color="primary" />,
    title: "Kanban tracking",
    body: "Move every application across stages with notes and outcomes.",
  },
  {
    icon: <RecordVoiceOverIcon color="primary" />,
    title: "Interview practice",
    body: "Record answers. Get STAR scoring, filler words, WPM, and AI feedback.",
  },
  {
    icon: <AccountTreeIcon color="primary" />,
    title: "Career path graph",
    body: "Adjacent and stretch roles with the exact skills to bridge.",
  },
  {
    icon: <MarkEmailReadIcon color="primary" />,
    title: "Learn from rejections",
    body: "Forward rejection emails — we extract the real reason and update your queue.",
  },
];

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        height: "100%",
        transition: "transform 0.2s, border-color 0.2s",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: "primary.main",
        },
      }}
    >
      <Box sx={{ mb: 1.5, "& .MuiSvgIcon-root": { fontSize: 36 } }}>{icon}</Box>
      <Typography variant="h6" component="h4" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Box>
  );
}

function Step({
  n,
  title,
  body,
}: {
  n: number;
  title: string;
  body: string;
}) {
  return (
    <Box sx={{ position: "relative", pl: { xs: 6, sm: 7 } }}>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          width: 44,
          height: 44,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontWeight: 700,
        }}
      >
        {n}
      </Box>
      <Typography variant="h6" component="h4" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Box>
  );
}

function MobileCarousel() {
  const [index, setIndex] = useState(0);
  const total = SLIDES.length;

  const go = (delta: number) =>
    setIndex((i) => (i + delta + total) % total);

  // Touch swipe
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart == null) return;
    const dx = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    setTouchStart(null);
  };

  const slide = SLIDES[index];

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        sx={{
          position: "relative",
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          p: { xs: 4, sm: 5 },
          minHeight: 320,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          background: "linear-gradient(135deg, rgba(166,215,133,0.10), rgba(90,140,62,0.04))",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: (theme) => `color-mix(in oklab, ${theme.palette.primary.main} 10%, transparent)`,
            mb: 2,
            "& .MuiSvgIcon-root": { fontSize: 36 },
          }}
        >
          {slide.icon}
        </Box>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
          {slide.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 360 }}>
          {slide.body}
        </Typography>

        <IconButton
          onClick={() => go(-1)}
          aria-label="Previous"
          sx={{ position: "absolute", left: 4, top: "50%", transform: "translateY(-50%)" }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={() => go(1)}
          aria-label="Next"
          sx={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)" }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Dots */}
      <Stack direction="row" spacing={1} sx={{ justifyContent: "center", mt: 2 }}>
        {SLIDES.map((_, i) => (
          <Box
            key={i}
            onClick={() => setIndex(i)}
            role="button"
            aria-label={`Go to slide ${i + 1}`}
            sx={{
              width: i === index ? 24 : 8,
              height: 8,
              borderRadius: 4,
              bgcolor: i === index ? "primary.main" : "divider",
              transition: "all 0.2s",
              cursor: "pointer",
            }}
          />
        ))}
      </Stack>

      <Stack spacing={1.5} sx={{ mt: 4 }}>
        <Button component={Link} to="/signup" variant="contained" size="large" fullWidth>
          Get started — free
        </Button>
        <Button component={Link} to="/login" variant="outlined" size="large" fullWidth>
          I have an account
        </Button>
      </Stack>
    </Box>
  );
}

function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!loading && user) navigate({ to: "/app/profile" });
  }, [user, loading, navigate]);

  const yearStr = useMemo(() => String(new Date().getFullYear()), []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", position: "relative" }}>
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        <Box
          sx={{
            position: "absolute",
            top: -120,
            left: -80,
            width: 480,
            height: 480,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(166,215,133,0.28), transparent 60%)",
            filter: "blur(20px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -160,
            right: -80,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(90,140,62,0.20), transparent 60%)",
            filter: "blur(24px)",
          }}
        />
      </Box>

      <Container component="main" maxWidth="lg" sx={{ position: "relative", py: { xs: 4, md: 8 } }}>
        {/* Nav */}
        <Stack
          direction="row"
          sx={{ alignItems: "center", justifyContent: "space-between", mb: { xs: 4, md: 8 } }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Box
              component="img"
              src={logo}
              alt="JobYoda"
              width={40}
              height={40}
              fetchPriority="high"
              decoding="async"
              sx={{ width: 40, height: 40, display: "block" }}
            />
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
              JobYoda
            </Typography>
          </Stack>
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
            <Button component={Link} to="/pricing" color="inherit" size={isMobileOrTablet ? "small" : "medium"}>
              Pricing
            </Button>
            <Button component={Link} to="/login" color="inherit" size={isMobileOrTablet ? "small" : "medium"}>
              Log in
            </Button>
            <Button component={Link} to="/signup" variant="contained" size={isMobileOrTablet ? "small" : "medium"}>
              Get started
            </Button>
          </Stack>
        </Stack>

        {isMobileOrTablet ? (
          /* MOBILE / TABLET — onboarding carousel */
          <Box>
            <Box sx={{ textAlign: "center" }}>
              <Chip label="AI-native career OS" color="primary" variant="outlined" sx={{ mb: 2 }} />
              <Typography
                variant="h3"
                component="h1"
                sx={{ fontSize: { xs: 30, sm: 40 }, lineHeight: 1.1, mb: 1.5, fontWeight: 700 }}
              >
                Land the right role — faster.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480, mx: "auto" }}>
                Score, tailor, track, and learn — all in one place.
              </Typography>
            </Box>
            <MobileCarousel />
            <Stack direction="row" spacing={2} sx={{ mt: 4, justifyContent: "center", flexWrap: "wrap", gap: 1 }}>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                <CheckCircleIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">No credit card</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                <CheckCircleIcon color="primary" fontSize="small" />
                <Typography variant="body2" color="text.secondary">Free tier forever</Typography>
              </Stack>
            </Stack>
          </Box>
        ) : (
          /* DESKTOP — full marketing page */
          <>
            {/* Hero */}
            <Box sx={{ maxWidth: 880 }}>
              <Chip label="AI-native career OS" color="primary" variant="outlined" sx={{ mb: 3 }} />
              <Typography
                variant="h2"
                component="h1"
                sx={{ fontSize: { md: 60 }, lineHeight: 1.05, mb: 2, fontWeight: 700 }}
              >
                Score yourself. Tailor everything. Land the role.
              </Typography>
              <Typography variant="h6" component="h2" color="text.secondary" sx={{ fontWeight: 400, mb: 4 }}>
                JobYoda scores you 1–10 against any JD, generates tailored materials, tracks every
                application, and learns from every rejection.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button component={Link} to="/signup" size="large" variant="contained">
                  Build my profile — free
                </Button>
                <Button
                  component={Link}
                  to="/pricing"
                  size="large"
                  variant="outlined"
                  sx={{
                    color: "text.primary",
                    borderColor: "primary.main",
                    "&:hover": { borderColor: "primary.dark", bgcolor: "action.hover" },
                  }}
                >
                  See pricing
                </Button>
              </Stack>
              <Stack direction="row" spacing={3} sx={{ mt: 4, flexWrap: "wrap", gap: 1.5 }}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                  <CheckCircleIcon color="primary" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">No credit card</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                  <CheckCircleIcon color="primary" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Free tier forever</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                  <CheckCircleIcon color="primary" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Your data, your control</Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Ingestion sources */}
            <Box sx={{ mt: { md: 12 } }}>
              <Typography variant="overline" color="text.secondary">
                Ingest your real career — not a manual form
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { sm: "repeat(4, 1fr)" },
                }}
              >
                {[
                  { icon: <DescriptionIcon />, label: "Resume PDF" },
                  { icon: <LinkedInIcon />, label: "LinkedIn" },
                  { icon: <GitHubIcon />, label: "GitHub" },
                  { icon: <LinkIcon />, label: "Portfolio URL" },
                ].map((s) => (
                  <Stack
                    key={s.label}
                    direction="row"
                    spacing={1.5}
                    sx={{
                      alignItems: "center",
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box sx={{ color: "primary.main", display: "flex" }}>{s.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {s.label}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Box>

            {/* Core features */}
            <Box sx={{ mt: { md: 12 } }}>
              <Typography variant="h3" sx={{ fontSize: { md: 40 }, mb: 1, fontWeight: 700 }}>
                Everything you need to land the right role
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 5, maxWidth: 720 }}>
                Eight tools, one verified profile. Every output grounded in your real experience.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gap: 2.5,
                  gridTemplateColumns: { md: "repeat(4, 1fr)" },
                }}
              >
                <FeatureCard
                  icon={<InsightsIcon color="primary" />}
                  title="1–10 match score"
                  body="Per-dimension fit across skills, experience, domain, and seniority."
                />
                <FeatureCard
                  icon={<DescriptionIcon color="primary" />}
                  title="Tailored resumes"
                  body="One-click rewrite leading with what the JD asks for."
                />
                <FeatureCard
                  icon={<AutoAwesomeIcon color="primary" />}
                  title="Cover letters"
                  body="JD-aligned letters in your voice, in seconds."
                />
                <FeatureCard
                  icon={<QuestionAnswerIcon color="primary" />}
                  title="Application Q&A"
                  body="Auto-answer common application questions from your profile."
                />
                <FeatureCard
                  icon={<ViewKanbanIcon color="primary" />}
                  title="Kanban tracking"
                  body="Stages, timeline, notes, and outcomes for every application."
                />
                <FeatureCard
                  icon={<RecordVoiceOverIcon color="primary" />}
                  title="Interview practice"
                  body="STAR scoring, filler words, WPM, and AI feedback."
                />
                <FeatureCard
                  icon={<AccountTreeIcon color="primary" />}
                  title="Career path graph"
                  body="Adjacent and stretch roles with skills to bridge."
                />
                <FeatureCard
                  icon={<MarkEmailReadIcon color="primary" />}
                  title="Rejection learning"
                  body="Forward rejections — we extract the signal automatically."
                />
              </Box>
            </Box>

            {/* How it works */}
            <Box sx={{ mt: { md: 14 } }}>
              <Typography variant="h3" sx={{ fontSize: { md: 40 }, mb: 1, fontWeight: 700 }}>
                How it works
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 5, maxWidth: 720 }}>
                From sign-up to first tailored application in under ten minutes.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: { md: 5 },
                  gridTemplateColumns: { md: "repeat(4, 1fr)" },
                }}
              >
                <Step n={1} title="Ingest profile" body="Drop your resume, link GitHub, LinkedIn, or portfolio." />
                <Step n={2} title="Add a job" body="Paste a JD or fetch by URL — we auto-extract the details." />
                <Step n={3} title="Score & generate" body="Get your fit score, then generate tailored materials." />
                <Step n={4} title="Track & learn" body="Move applications across kanban. Forward rejections." />
              </Box>
            </Box>

            {/* Secondary feature row */}
            <Box sx={{ mt: { md: 14 } }}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2.5,
                  gridTemplateColumns: { md: "1fr 1fr 1fr" },
                }}
              >
                <FeatureCard
                  icon={<FlagIcon color="primary" />}
                  title="Goals"
                  body="Pin target roles and track skill progress toward each."
                />
                <FeatureCard
                  icon={<SchoolIcon color="primary" />}
                  title="Learning queue"
                  body="Auto-prioritized skills from JD gaps and rejection signals."
                />
                <FeatureCard
                  icon={<BoltIcon color="primary" />}
                  title="Autofill bookmarklet"
                  body="One-click autofill for application forms on any site."
                />
              </Box>
            </Box>

            {/* CTA */}
            <Box
              sx={{
                mt: { md: 14 },
                p: { md: 6 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                background:
                  "linear-gradient(135deg, rgba(166,215,133,0.10), rgba(90,140,62,0.05))",
              }}
            >
              <Stack
                direction={{ md: "row" }}
                spacing={3}
                sx={{ alignItems: { md: "center" }, justifyContent: "space-between" }}
              >
                <Box sx={{ maxWidth: 560 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    Run your job search like a system.
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Free to start. Build your profile in minutes — score your first JD today.
                  </Typography>
                </Box>
                <Stack direction={{ sm: "row" }} spacing={1.5}>
                  <Button component={Link} to="/signup" size="large" variant="contained">
                    Get started — free
                  </Button>
                  <Button
                    component={Link}
                    to="/login"
                    size="large"
                    variant="outlined"
                    sx={{
                      color: "text.primary",
                      borderColor: "primary.main",
                      "&:hover": { borderColor: "primary.dark", bgcolor: "action.hover" },
                    }}
                  >
                    I have an account
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </>
        )}

        {/* Footer */}
        <Divider sx={{ mt: { xs: 6, md: 12 }, mb: 3 }} />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", pb: 4 }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Box
              component="img"
              src={logo}
              alt="JobYoda"
              width={24}
              height={24}
              sx={{ width: 24, height: 24 }}
            />
            <Typography variant="body2" color="text.secondary">
              © {yearStr} JobYoda
            </Typography>
          </Stack>
          <Stack direction="row" spacing={{ xs: 1, sm: 2 }} sx={{ flexWrap: "wrap" }}>
            <Button component={Link} to="/pricing" size="small" color="inherit">Pricing</Button>
            <Button component={Link} to="/support" size="small" color="inherit">Support</Button>
            <Button component={Link} to="/terms" size="small" color="inherit">Terms</Button>
            <Button component={Link} to="/privacy" size="small" color="inherit">Privacy</Button>
            <Button component={Link} to="/refunds" size="small" color="inherit">Refunds</Button>
            <Button component={Link} to="/login" size="small" color="inherit">Log in</Button>
            <Button component={Link} to="/signup" size="small" color="inherit">Sign up</Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
